import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import api from '../../api/api';
import '../../styles/UserAccessControl.css';

export default function UserAccessControl() {
  const [data, setData] = useState(null);
  const [target, setTarget] = useState('role:qc');
  const [draft, setDraft] = useState({});
  const [busy, setBusy] = useState(false);
  const selectedUser = target.startsWith('user:');
  const id = target.split(':')[1];

  const load = async (preserve = true) => {
    const { data: next } = await api.get('users/access/');
    setData(next);
    if (!preserve) setTarget(`role:${next.roles.find((item) => item.role !== 'admin')?.role || 'qc'}`);
    const item = target.startsWith('user:')
      ? next.users.find((user) => user.id === Number(id))
      : next.roles.find((role) => role.role === id);
    setDraft(selectedUser ? (item?.overrides || {}) : (item?.rights || {}));
  };

  useEffect(() => { load(false).catch(() => Swal.fire('Admin access required', 'Only administrators can edit system access.', 'error')); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!data) return;
    const item = selectedUser ? data.users.find((user) => user.id === Number(id)) : data.roles.find((role) => role.role === id);
    setDraft(selectedUser ? (item?.overrides || {}) : (item?.rights || {}));
  }, [target, data, selectedUser, id]);

  const groups = useMemo(() => (data?.capabilities || []).reduce((all, capability) => {
    (all[capability.module] ||= []).push(capability); return all;
  }, {}), [data]);

  const save = async () => {
    setBusy(true);
    try {
      const supportedCodes = new Set((data?.capabilities || []).map((capability) => capability.code));
      const rights = Object.fromEntries(Object.entries(draft).filter(([code, value]) => supportedCodes.has(code) && typeof value === 'boolean'));
      await api.put('users/access/', { ...(selectedUser ? { user: Number(id) } : { role: id }), rights });
      await load();
      window.dispatchEvent(new Event('smartharvest:access-changed'));
      Swal.fire({ title: 'Access policy saved', icon: 'success', timer: 1200, showConfirmButton: false });
    } catch (error) {
      Swal.fire('Could not save access', Object.values(error.response?.data || {}).flat().join(' ') || 'Please try again.', 'error');
    } finally { setBusy(false); }
  };

  const changeRight = (code, value) => setDraft((current) => {
    const next = { ...current };
    const module = code.split('.')[0];
    const viewCode = module === 'clients' ? 'clients.portal.view' : `${module}.view`;
    if (value === null) delete next[code];
    else next[code] = value;
    if (code === viewCode && value === false) {
      data.capabilities.filter((item) => item.code.startsWith(`${module}.`) && item.code !== code).forEach((item) => { next[item.code] = false; });
    } else if (code !== viewCode && value === true) {
      next[viewCode] = true;
    }
    return next;
  });

  if (!data) return <section className="ua-studio"><p>Loading access policies…</p></section>;
  const selectedName = selectedUser ? data.users.find((user) => user.id === Number(id))?.name : data.roles.find((role) => role.role === id)?.name;
  const inherited = selectedUser ? data.users.find((user) => user.id === Number(id))?.rights || {} : {};

  return <section className="ua-studio">
    <header className="ua-heading"><div><span>ADMIN · SYSTEM POLICY</span><h2>Access Control Studio</h2><p>Map module and action rights to roles, then apply individual overrides to specific user accounts.</p></div><button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Publish policy'}</button></header>
    <div className="ua-layout">
      <aside className="ua-rail"><div className="ua-rail-label">POLICY TARGET</div><button className={!selectedUser ? 'selected' : ''} onClick={() => setTarget(`role:${id && !selectedUser ? id : data.roles.find((item) => item.role !== 'admin')?.role}`)}>Role policy</button><button className={selectedUser ? 'selected' : ''} onClick={() => setTarget(`user:${data.users.find((item) => item.role !== 'admin')?.id || ''}`)}>User override</button>
        <label>{selectedUser ? 'Select user' : 'Select role'}<select value={target} onChange={(event) => setTarget(event.target.value)}>
          {selectedUser ? data.users.filter((user) => user.role !== 'admin').map((user) => <option key={user.id} value={`user:${user.id}`}>{user.name} · {user.role.replaceAll('_', ' ')}</option>) : data.roles.filter((role) => role.role !== 'admin').map((role) => <option key={role.role} value={`role:${role.role}`}>{role.name}</option>)}
        </select></label>
        <div className="ua-admin-lock"><strong>Administrator</strong><span>Full system access · fixed</span></div>
      </aside>
      <div className="ua-matrix"><div className="ua-matrix-head"><div><span>{selectedUser ? 'INDIVIDUAL OVERRIDE' : 'ROLE RIGHTS'}</span><h3>{selectedName || 'Choose a target'}</h3><p>{selectedUser ? 'Inherit follows the assigned role policy. Allow or Deny only where this user needs an exception.' : id === 'key_accounts_manager' ? 'Client tools are included by default. Grant additional system capabilities here; the selected rights appear in Key Accounts Managers’ navigation.' : 'Changes apply to every user assigned this role.'}</p></div><b>{Object.values(draft).filter(Boolean).length} enabled</b></div>
        {Object.entries(groups).map(([module, rights]) => <section className="ua-group" key={module}><h4>{module}<small>{rights.filter((item) => (selectedUser ? (Object.hasOwn(draft, item.code) ? draft[item.code] : inherited[item.code]) : draft[item.code])).length}/{rights.length} enabled</small></h4>{rights.map((right) => {
          const effective = Object.hasOwn(draft, right.code) ? draft[right.code] : (selectedUser ? inherited[right.code] : false);
          return <div className="ua-row" key={right.code}><div><strong>{right.label}</strong><p>{right.description}</p><code>{right.code}</code></div>{selectedUser ? <select value={Object.hasOwn(draft, right.code) ? String(draft[right.code]) : 'inherit'} onChange={(event) => changeRight(right.code, event.target.value === 'inherit' ? null : event.target.value === 'true')}><option value="inherit">Inherit · {effective ? 'Allowed' : 'Denied'}</option><option value="true">Allow</option><option value="false">Deny</option></select> : <button className={`ua-toggle ${effective ? 'on' : ''}`} aria-pressed={effective} onClick={() => changeRight(right.code, !draft[right.code])}>{effective ? 'Allowed' : 'Denied'}</button>}</div>;
        })}</section>)}
      </div>
    </div>
  </section>;
}
